import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import ChartPanel from "../components/ChartPanel"
import ArbitrageTable from "../components/ArbitrageTable"

const API = process.env.REACT_APP_API_URL || "http://localhost:8000"

function authHeaders() {
    const token = localStorage.getItem("token")
    return { headers: { Authorization: `Bearer ${token}` } }
}

const ALL_EXCHANGES = ["binance", "bybit", "kraken", "coinbase", "okx"]

function Dashboard() {
    const nav = useNavigate()

    const [data, setData] = useState([])
    const [markets, setMarkets] = useState([])
    const [triangular, setTriangular] = useState(false)

    const [tradingFees,    setTradingFees]    = useState(false)
    const [withdrawalFees, setWithdrawalFees] = useState(false)
    const [minSpread, setMinSpread] = useState(0.3)

    const [settingsLoaded, setSettingsLoaded] = useState(false)
    const [selectedSymbol, setSelectedSymbol] = useState(null)
    const [linkingTelegram, setLinkingTelegram] = useState(false)
    const [billingLoading, setBillingLoading] = useState(false)

    const [plan, setPlan] = useState("trial")
    const [isSubscribed, setIsSubscribed] = useState(false)

    const logout = () => {
        localStorage.removeItem("token")
        nav("/")
    }

    const toggle = (m) => {
        setMarkets(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m])
    }

    const fetchBillingStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/billing/status`, authHeaders())
            setPlan(res.data.plan || "trial")
            setIsSubscribed(res.data.is_subscribed || false)
        } catch (err) {
            if (err.response?.status === 401) nav("/login")
        }
    }, [nav])

    const fetchSettings = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/settings`, authHeaders())
            setMarkets(res.data.markets || [])
            setTriangular(res.data.triangular || false)
            setTradingFees(res.data.trading_fees || false)
            setWithdrawalFees(res.data.withdrawal_fees || res.data.withdrawal || false)
            setMinSpread(res.data.min_spread || 0.3)
            setSettingsLoaded(true)
        } catch (err) {
            if (err.response?.status === 401) nav("/login")
        }
    }, [nav])

    const saveSettings = async () => {
        try {
            await axios.post(
                `${API}/settings`,
                {
                    markets,
                    triangular,
                    trading_fees:    tradingFees,
                    withdrawal_fees: withdrawalFees,
                    withdrawal:      withdrawalFees,
                    min_spread:      minSpread,
                },
                authHeaders()
            )
            alert("Settings saved")
        } catch (err) {
            console.error(err)
            if (err.response?.status === 401) nav("/login")
        }
    }

    const fetchData = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/arbitrage`, authHeaders())
            setData(res.data)
        } catch (err) {
            if (err.response?.status === 401) nav("/login")
        }
    }, [nav])

    const linkTelegram = async () => {
        setLinkingTelegram(true)
        try {
            const res = await axios.post(`${API}/telegram/link`, {}, authHeaders())
            if (res.data.url) {
                window.open(res.data.url, "_blank")
            } else {
                alert("Could not generate Telegram link. Please try again in a moment.")
            }
        } catch (err) {
            const msg = err.response?.data?.detail || "Could not generate Telegram link."
            alert(msg)
        } finally {
            setLinkingTelegram(false)
        }
    }

    const upgradeToPro = async () => {
        setBillingLoading(true)
        try {
            const res = await axios.post(`${API}/billing/create-checkout`, {}, authHeaders())
            if (res.data.url) {
                window.location.href = res.data.url
            } else {
                alert("Could not create checkout. Please try again.")
            }
        } catch (err) {
            const msg = err.response?.data?.detail || "Checkout error. Please try again."
            alert(msg)
        } finally {
            setBillingLoading(false)
        }
    }

    useEffect(() => {
        fetchSettings()
        fetchBillingStatus()
    }, [fetchSettings, fetchBillingStatus])

    useEffect(() => {
        fetchData()
        const interval = setInterval(fetchData, 3000)
        return () => clearInterval(interval)
    }, [fetchData])

    const symbols = [...new Set(data.map(d => d.symbol))]
    const chartData = selectedSymbol
        ? data.filter(d => d.symbol === selectedSymbol)
        : data.slice(0, 50)

    return (
        <div style={{ padding: "32px", maxWidth: "1200px", margin: "0 auto", color: "#e8eaf0", background: "#060b23", minHeight: "100vh" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
                <h2 style={{ color: "#00ffc6", margin: 0 }}>Crypto Arbitrage Dashboard</h2>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    {isSubscribed ? (
                        <button
                            onClick={async () => {
                                if (!window.confirm("Cancel your Pro subscription? You'll keep access until the end of your billing period, then revert to free.")) return
                                try {
                                    await axios.post(`${API}/billing/cancel`, {}, authHeaders())
                                    alert("Subscription cancellation requested. You'll receive a confirmation email.")
                                    fetchBillingStatus()
                                } catch (err) {
                                    alert(err.response?.data?.detail || "Could not cancel — please contact support.")
                                }
                            }}
                            style={{ background: "rgba(0,255,198,0.15)", color: "#00ffc6", padding: "8px 16px", border: "1px solid #00ffc6", borderRadius: "8px", fontWeight: 600, fontSize: 14, cursor: "pointer" }}
                            title="Click to cancel subscription"
                        >
                            ✓ Pro (cancel)
                        </button>
                    ) : (
                        <button
                            onClick={upgradeToPro}
                            disabled={billingLoading}
                            style={{ background: "#00ffc6", color: "#060b23", padding: "8px 16px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600 }}
                        >
                            {billingLoading ? "..." : "Upgrade to Pro $29"}
                        </button>
                    )}
                    <button onClick={() => nav("/settings")} style={{ background: "transparent", color: "#cbd5e1", padding: "8px 16px", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", cursor: "pointer" }}>
                        ⚙ Settings
                    </button>
                    <button onClick={logout} style={{ background: "transparent", color: "#cbd5e1", padding: "8px 16px", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", cursor: "pointer" }}>
                        Logout
                    </button>
                </div>
            </div>

            <div style={{ background: "#0d1433", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
                <h3 style={{ marginTop: 0, color: "#fff" }}>Filters</h3>

                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ minWidth: 220 }}>Minimum spread % (alert threshold):</span>
                        <input
                            type="number"
                            step="0.05"
                            min="0"
                            max="5"
                            value={minSpread}
                            onChange={e => setMinSpread(parseFloat(e.target.value) || 0)}
                            style={{ width: 80, padding: "6px 8px", background: "#060b23", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, color: "#fff" }}
                        />
                    </label>
                </div>

                <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={tradingFees}
                            onChange={() => setTradingFees(t => !t)}
                            style={{ width: 18, height: 18 }}
                        />
                        <span>Deduct trading fees from spreads (shows real net profit)</span>
                    </label>
                </div>

                <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={withdrawalFees}
                            onChange={() => setWithdrawalFees(w => !w)}
                            style={{ width: 18, height: 18 }}
                        />
                        <span>Deduct withdrawal fees from spreads (cross-exchange transfers)</span>
                    </label>
                </div>

                <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={triangular}
                            onChange={() => setTriangular(t => !t)}
                            style={{ width: 18, height: 18 }}
                        />
                        <span>Show triangular arbitrage (single-exchange A→B→C→A cycles)</span>
                    </label>
                </div>
            </div>

            <div style={{ background: "#0d1433", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "20px", marginBottom: "24px" }}>
                <h3 style={{ marginTop: 0, color: "#fff" }}>Markets ({markets.length} of {ALL_EXCHANGES.length} selected)</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                    {ALL_EXCHANGES.map(ex => (
                        <label key={ex} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "6px 12px", background: markets.includes(ex) ? "rgba(0,255,198,0.1)" : "transparent", border: `1px solid ${markets.includes(ex) ? "#00ffc6" : "rgba(255,255,255,0.1)"}`, borderRadius: "8px" }}>
                            <input
                                type="checkbox"
                                checked={markets.includes(ex)}
                                onChange={() => toggle(ex)}
                            />
                            <span style={{ color: markets.includes(ex) ? "#00ffc6" : "#cbd5e1", textTransform: "capitalize" }}>{ex}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div style={{ display: "flex", gap: "10px", marginBottom: "24px" }}>
                <button onClick={saveSettings} disabled={!settingsLoaded} style={{ background: "#00ffc6", color: "#060b23", padding: "10px 20px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600 }}>
                    Save Settings
                </button>
                <button onClick={linkTelegram} disabled={linkingTelegram} style={{ background: "#0088cc", color: "white", padding: "10px 20px", border: "none", borderRadius: "8px", cursor: "pointer" }}>
                    {linkingTelegram ? "Generating..." : "🔗 Connect Telegram"}
                </button>
            </div>

            <h3 style={{ color: "#fff" }}>
                Spread Chart{" "}
                <select
                    value={selectedSymbol || ""}
                    onChange={e => setSelectedSymbol(e.target.value || null)}
                    style={{ marginLeft: "12px", fontSize: "14px", background: "#060b23", color: "#fff", border: "1px solid rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: "6px" }}
                >
                    <option value="">All (latest 50)</option>
                    {symbols.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </h3>

            <ChartPanel data={chartData} />

            <h3 style={{ color: "#fff", marginTop: "32px" }}>Opportunities ({data.length})</h3>
            <ArbitrageTable data={data} />
        </div>
    )
}

export default Dashboard

